import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ArrowLeftRight, Building2, Check, Power, RotateCcw } from 'lucide-react';
import type { Paginated, PlatformBusinessRow } from '@worki/shared';
import { api } from '../../lib/api.js';
import { currentLocale } from '../../i18n.js';
import { formatDate } from '../../lib/format.js';
import { EmptyState, Spinner } from '../../components/ui.js';
import { AdminCard, Row, useAdminAction } from './parts.js';

/**
 * ADM-003 — the two lists that are about the door rather than about the money.
 *
 * They sit together because they are the same decision seen from either side:
 * an account that has not been let in yet, and one that was let in and is not
 * any more. Both actions here open a door; the one that closes it lives on the
 * business itself, where the person deciding can see what the account does.
 */
function useAccessList(access: 'pending' | 'suspended') {
  return useQuery({
    queryKey: ['platform', 'access', access],
    queryFn: () => api.get<Paginated<PlatformBusinessRow>>(`/platform/businesses?access=${access}&pageSize=100`),
  });
}

/**
 * ADM-003 — accounts whose package has run out.
 *
 * They belong beside the two lists above because they are the third way an
 * account stops working, and the only one nobody at Worki decided: a
 * cancellation and a lapsed trial both leave a business signed in and unable to
 * do anything, and until now neither appeared on a screen. The door is not the
 * fix here — the plan is — so the row leads to the business record rather than
 * offering a button that would open a door that is not shut.
 */
function useEndedList() {
  return useQuery({
    queryKey: ['platform', 'access', 'ended'],
    queryFn: () => api.get<Paginated<PlatformBusinessRow>>('/platform/businesses?status=ended&pageSize=100'),
  });
}

/** Everything a door change has to refresh, in one place both screens use. */
export const ACCESS_KEYS = [
  ['platform', 'access'],
  ['platform', 'businesses'],
  ['platform', 'business'],
  ['platform', 'summary'],
  ['platform', 'notifications'],
];

export function ApprovalsSection() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();
  const pending = useAccessList('pending');
  const suspended = useAccessList('suspended');
  const ended = useEndedList();
  const action = useAdminAction(ACCESS_KEYS);

  const open = (id: string, successKey: string) =>
    action.run(() => api.post(`/platform/businesses/${id}/access`, { access: 'active' }), successKey);

  const list = (
    query: UseQueryResult<Paginated<PlatformBusinessRow>>,
    emptyKey: string,
    render: (row: PlatformBusinessRow) => ReactNode,
  ) => {
    if (query.isLoading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      );
    }
    const rows = query.data?.items ?? [];
    if (rows.length === 0) return <EmptyState icon={<Building2 className="h-7 w-7" />} title={t(emptyKey)} />;
    return <div className="space-y-2">{rows.map(render)}</div>;
  };

  const openButton = (label: string, icon: ReactNode, onClick: () => void, primary: boolean) => (
    <button
      type="button"
      onClick={onClick}
      disabled={action.busy}
      className={
        primary
          ? 'h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity'
          : 'h-8 px-3 rounded-lg border border-border bg-card text-xs font-medium flex items-center gap-1.5 hover:bg-muted disabled:opacity-50 transition-colors'
      }
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <AdminCard title={t('admin.approvals.pending')} hint={t('admin.approvals.hint')}>
        {list(pending, 'admin.approvals.pendingEmpty', (row) => (
          <Row
            key={row.id}
            icon={<Building2 className="h-4 w-4" aria-hidden />}
            title={row.name}
            subtitle={row.email ?? row.phone ?? ''}
            meta={`${t('admin.approvals.joined')} ${formatDate(row.createdAt, locale)}`}
            actions={openButton(
              t('admin.approvals.approve'),
              <Check className="h-3.5 w-3.5" />,
              () => void open(row.id, 'admin.approvals.approved'),
              true,
            )}
          />
        ))}
      </AdminCard>

      <AdminCard title={t('admin.approvals.suspended')}>
        {list(suspended, 'admin.approvals.suspendedEmpty', (row) => (
          <Row
            key={row.id}
            icon={<Power className="h-4 w-4" aria-hidden />}
            title={row.name}
            subtitle={row.email ?? row.phone ?? ''}
            actions={openButton(
              t('admin.approvals.restore'),
              <RotateCcw className="h-3.5 w-3.5" />,
              () => void open(row.id, 'admin.approvals.restored'),
              false,
            )}
          />
        ))}
      </AdminCard>

      <AdminCard title={t('admin.approvals.ended')} hint={t('admin.approvals.endedHint')}>
        {list(ended, 'admin.approvals.endedEmpty', (row) => (
          <Row
            key={row.id}
            icon={<ArrowLeftRight className="h-4 w-4" aria-hidden />}
            title={row.name}
            subtitle={row.email ?? row.phone ?? ''}
            meta={t(`billing.status.${row.status}`, { defaultValue: row.status })}
            actions={openButton(
              t('admin.notifications.open'),
              <Building2 className="h-3.5 w-3.5" />,
              () => navigate(`/platform/businesses?open=${row.id}`),
              false,
            )}
          />
        ))}
      </AdminCard>
    </div>
  );
}
